/**
 * Authentication Token Management Utility
 * 
 * This module handles separate token storage for corporate and candidate users,
 * allowing simultaneous logins to both portals.
 */
import cookieCutter from "cookie-cutter";

// Cookie names
export const CORPORATE_TOKEN_KEY = "corporateToken";
export const CANDIDATE_TOKEN_KEY = "candidateToken";

// Legacy token key for backward compatibility
export const LEGACY_TOKEN_KEY = "token";

/**
 * Get the corporate user's authentication token
 */
export const getCorporateToken = () => {
  if (typeof window === 'undefined') return null;
  const token = cookieCutter.get(CORPORATE_TOKEN_KEY);
  // Fallback to legacy token if corporate token not found
  if (!token) {
    return cookieCutter.get(LEGACY_TOKEN_KEY);
  }
  return token;
};

/**
 * Set the corporate user's authentication token
 */
export const setCorporateToken = (token, options = {}) => {
  // Always set cookie at root path so it's accessible from all pages
  cookieCutter.set(CORPORATE_TOKEN_KEY, token, { ...options, path: "/" });
  // Also clear legacy token to avoid conflicts
  if (token) {
    cookieCutter.set(LEGACY_TOKEN_KEY, "", { expires: new Date(0), path: "/" });
  }
};

/**
 * Clear the corporate user's authentication token
 */
export const clearCorporateToken = () => {
  cookieCutter.set(CORPORATE_TOKEN_KEY, "", { expires: new Date(0), path: "/" });
  cookieCutter.set(CORPORATE_TOKEN_KEY, "", { expires: new Date(0), path: "/corporate" });
  // Also clear legacy token
  cookieCutter.set(LEGACY_TOKEN_KEY, "", { expires: new Date(0), path: "/" });
  cookieCutter.set(LEGACY_TOKEN_KEY, "", { expires: new Date(0), path: "/corporate" });
};

/**
 * Get the candidate user's authentication token
 */
export const getCandidateToken = () => {
  if (typeof window === 'undefined') return null;
  const token = cookieCutter.get(CANDIDATE_TOKEN_KEY);
  // Fallback to legacy token if candidate token not found
  if (!token) {
    return cookieCutter.get(LEGACY_TOKEN_KEY);
  }
  return token;
};

/**
 * Set the candidate user's authentication token
 */
export const setCandidateToken = (token, options = {}) => {
  // Always set cookie at root path so it's accessible from all pages
  cookieCutter.set(CANDIDATE_TOKEN_KEY, token, { ...options, path: "/" });
  // Also clear legacy token to avoid conflicts
  if (token) {
    cookieCutter.set(LEGACY_TOKEN_KEY, "", { expires: new Date(0), path: "/" });
  }
};

/**
 * Clear the candidate user's authentication token
 */
export const clearCandidateToken = () => {
  cookieCutter.set(CANDIDATE_TOKEN_KEY, "", { expires: new Date(0), path: "/" });
  cookieCutter.set(CANDIDATE_TOKEN_KEY, "", { expires: new Date(0), path: "/candidate" });
  // Also clear legacy token
  cookieCutter.set(LEGACY_TOKEN_KEY, "", { expires: new Date(0), path: "/" });
};

/**
 * Check if a corporate user is logged in
 */
export const isCorporateLoggedIn = () => {
  const token = getCorporateToken();
  return !!token && token.length > 0;
};

/**
 * Check if a candidate user is logged in
 */
export const isCandidateLoggedIn = () => {
  const token = getCandidateToken();
  return !!token && token.length > 0;
};

/**
 * Clear all authentication tokens (for complete logout)
 */
export const clearAllTokens = () => {
  clearCorporateToken();
  clearCandidateToken();
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("queryToken");
};

/**
 * Get token based on current path context
 * Useful for API calls that need to determine which token to use
 */
export const getContextualToken = () => {
  if (typeof window === "undefined") return null;
  
  const path = window.location.pathname;
  if (path.startsWith("/candidate")) {
    return getCandidateToken();
  } else if (path.startsWith("/corporate") || path.startsWith("/admin")) {
    return getCorporateToken();
  }
  
  // Default: try corporate first, then candidate
  return getCorporateToken() || getCandidateToken();
};
